import { pmContentToY, readRecords, readRecord, writeRecord } from './chunk-TCGBD5LJ.js';
import { FORMAT_VERSION, ACCOUNT_ROOTS, SPACE_ROOTS, NOTEBOOK_ROOTS, NOTE_ROOTS, DEFAULT_NOTE_STYLE, sharedRefKey } from './chunk-PP2CXEGZ.js';
import { uuidv7 } from './chunk-RPUENDSP.js';
import * as Y from 'yjs';

function accountRoots(doc) {
  return {
    meta: doc.getMap(ACCOUNT_ROOTS.meta),
    spaces: doc.getMap(ACCOUNT_ROOTS.spaces),
    notebooks: doc.getMap(ACCOUNT_ROOTS.notebooks),
    settings: doc.getMap(ACCOUNT_ROOTS.settings),
    starred: doc.getArray(ACCOUNT_ROOTS.starred),
    tabs: doc.getArray(ACCOUNT_ROOTS.tabs),
    shared: doc.getMap(ACCOUNT_ROOTS.shared),
    trust: doc.getMap(ACCOUNT_ROOTS.trust),
    sharedSpaces: doc.getMap(ACCOUNT_ROOTS.sharedSpaces)
  };
}
function spaceRoots(doc) {
  return {
    meta: doc.getMap(SPACE_ROOTS.meta),
    notebooks: doc.getMap(SPACE_ROOTS.notebooks)
  };
}
function notebookRoots(doc) {
  return {
    meta: doc.getMap(NOTEBOOK_ROOTS.meta),
    folders: doc.getMap(NOTEBOOK_ROOTS.folders),
    notes: doc.getMap(NOTEBOOK_ROOTS.notes),
    tags: doc.getMap(NOTEBOOK_ROOTS.tags)
  };
}
function noteRoots(doc) {
  return {
    content: doc.getXmlFragment(NOTE_ROOTS.content),
    meta: doc.getMap(NOTE_ROOTS.meta),
    strokes: doc.getMap(NOTE_ROOTS.strokes),
    comments: doc.getMap(NOTE_ROOTS.comments)
  };
}
function createAccountDoc(options = {}) {
  const doc = options.doc ?? new Y.Doc();
  doc.transact(() => {
    accountRoots(doc).meta.set("formatVersion", FORMAT_VERSION);
  });
  return doc;
}
function createSpaceDoc(spaceId, options = {}) {
  const doc = options.doc ?? new Y.Doc();
  doc.transact(() => {
    const meta = spaceRoots(doc).meta;
    meta.set("formatVersion", FORMAT_VERSION);
    meta.set("spaceId", spaceId);
    meta.set("name", options.name ?? "");
    meta.set("icon", options.icon ?? null);
    meta.set("color", options.color ?? null);
  });
  return doc;
}
function getSpaceMeta(doc) {
  const m = spaceRoots(doc).meta;
  return {
    formatVersion: m.get("formatVersion") ?? FORMAT_VERSION,
    spaceId: String(m.get("spaceId") ?? ""),
    name: String(m.get("name") ?? ""),
    icon: m.get("icon") ?? null,
    color: m.get("color") ?? null
  };
}
function setSpaceMeta(doc, fields) {
  const m = spaceRoots(doc).meta;
  doc.transact(() => {
    for (const [k, v] of Object.entries(fields)) {
      if (v === void 0) continue;
      if (m.get(k) !== v) m.set(k, v);
    }
  });
}
function createNotebookDoc(notebookId, options = {}) {
  const doc = options.doc ?? new Y.Doc();
  doc.transact(() => {
    const meta = notebookRoots(doc).meta;
    meta.set("formatVersion", FORMAT_VERSION);
    meta.set("notebookId", notebookId);
    meta.set("name", options.name ?? "");
    meta.set("icon", options.icon ?? null);
    meta.set("space", options.space ?? null);
  });
  return doc;
}
function getNotebookMeta(doc) {
  const m = notebookRoots(doc).meta;
  return {
    formatVersion: m.get("formatVersion") ?? FORMAT_VERSION,
    notebookId: String(m.get("notebookId") ?? ""),
    name: String(m.get("name") ?? ""),
    icon: m.get("icon") ?? null,
    space: m.get("space") ?? null
  };
}
function setNotebookMeta(doc, fields) {
  const m = notebookRoots(doc).meta;
  doc.transact(() => {
    for (const [k, v] of Object.entries(fields)) {
      if (v === void 0) continue;
      if (JSON.stringify(m.get(k)) !== JSON.stringify(v)) m.set(k, v);
    }
  });
}
function createNoteDoc(options = {}) {
  const doc = options.doc ?? new Y.Doc();
  const newId = options.newId ?? (() => uuidv7());
  const title = {
    type: "title",
    attrs: { blockId: newId() },
    ...options.title ? { content: [{ type: "text", text: options.title }] } : {}
  };
  const body = (options.blocks ?? [{ type: "paragraph" }]).map((b) => withBlockIds(b, newId));
  doc.transact(() => {
    const { content, meta } = noteRoots(doc);
    meta.set("formatVersion", FORMAT_VERSION);
    meta.set("style", { ...DEFAULT_NOTE_STYLE, ...options.meta?.style ?? {} });
    meta.set("cover", options.meta?.cover ?? null);
    meta.set("backdrop", options.meta?.backdrop ?? null);
    content.insert(0, pmContentToY([title, ...body]));
  });
  return doc;
}
var NON_BLOCK = /* @__PURE__ */ new Set(["text", "noteLink", "tag", "mention", "hardBreak", "doc"]);
function withBlockIds(node, newId = () => uuidv7()) {
  if (NON_BLOCK.has(node.type)) {
    return node.content ? { ...node, content: node.content.map((c) => withBlockIds(c, newId)) } : node;
  }
  const attrs = { ...node.attrs ?? {} };
  if (typeof attrs.blockId !== "string" || attrs.blockId === "") attrs.blockId = newId();
  if (node.type === "taskItem" && (typeof attrs.taskId !== "string" || attrs.taskId === "")) {
    attrs.taskId = newId();
  }
  const out = { ...node, attrs };
  if (node.content) out.content = node.content.map((c) => withBlockIds(c, newId));
  return out;
}
function getFormatVersion(doc) {
  const v = doc.getMap("meta").get("formatVersion");
  return typeof v === "number" ? v : null;
}
function isReadOnlyFormat(doc, supported = FORMAT_VERSION) {
  const v = getFormatVersion(doc);
  return v !== null && v > supported;
}
function rootNames(kind) {
  switch (kind) {
    case "account":
      return Object.values(ACCOUNT_ROOTS);
    case "notebook":
      return Object.values(NOTEBOOK_ROOTS);
    case "note":
      return Object.values(NOTE_ROOTS);
    case "space":
      return Object.values(SPACE_ROOTS);
  }
}
function sortByOrder(items) {
  return items.sort((a, b) => a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}
function patchRecord(map, id, patch) {
  if (!(map.get(id) instanceof Y.Map)) return false;
  writeRecord(map, id, { ...patch, id });
  return true;
}
var getSpaces = (doc) => sortByOrder(readRecords(accountRoots(doc).spaces));
var getSpace = (doc, id) => readRecord(accountRoots(doc).spaces, id);
var putSpace = (doc, space) => void writeRecord(accountRoots(doc).spaces, space.id, space);
var patchSpace = (doc, id, patch) => patchRecord(accountRoots(doc).spaces, id, patch);
var deleteSpace = (doc, id) => accountRoots(doc).spaces.delete(id);
var getNotebookRefs = (doc, spaceId) => sortByOrder(readRecords(accountRoots(doc).notebooks)).filter(
  (n) => spaceId === void 0 || n.spaceId === spaceId
);
var getNotebookRef = (doc, id) => readRecord(accountRoots(doc).notebooks, id);
var putNotebookRef = (doc, ref) => void writeRecord(accountRoots(doc).notebooks, ref.id, ref);
var patchNotebookRef = (doc, id, patch) => patchRecord(accountRoots(doc).notebooks, id, patch);
var deleteNotebookRef = (doc, id) => accountRoots(doc).notebooks.delete(id);
function getSettings(doc) {
  return accountRoots(doc).settings.toJSON();
}
function setSetting(doc, key, value) {
  const settings = accountRoots(doc).settings;
  if (value === void 0) settings.delete(key);
  else settings.set(key, value);
}
function getSharedRefs(doc, spaceId) {
  return readRecords(accountRoots(doc).shared).filter((r) => spaceId === void 0 || r.spaceId === spaceId).sort((a, b) => a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}
var getSharedRef = (doc, kind, id) => readRecord(accountRoots(doc).shared, sharedRefKey(kind, id));
var putSharedRef = (doc, ref) => void writeRecord(accountRoots(doc).shared, sharedRefKey(ref.kind, ref.id), ref);
var deleteSharedRef = (doc, kind, id) => accountRoots(doc).shared.delete(sharedRefKey(kind, id));
function getSharedSpaceRefs(doc) {
  const out = [];
  accountRoots(doc).sharedSpaces.forEach((v, spaceId) => {
    if (v instanceof Y.Map) out.push({ ...v.toJSON(), spaceId });
  });
  return out.sort((a, b) => a.order - b.order || (a.spaceId < b.spaceId ? -1 : 1));
}
var putSharedSpaceRef = (doc, spaceId, ref) => void writeRecord(accountRoots(doc).sharedSpaces, spaceId, ref);
var deleteSharedSpaceRef = (doc, spaceId) => accountRoots(doc).sharedSpaces.delete(spaceId);
var getSpaceNotebookRefs = (doc) => sortByOrder(readRecords(spaceRoots(doc).notebooks));
var getSpaceNotebookRef = (doc, id) => readRecord(spaceRoots(doc).notebooks, id);
var putSpaceNotebookRef = (doc, ref) => void writeRecord(spaceRoots(doc).notebooks, ref.id, ref);
var patchSpaceNotebookRef = (doc, id, patch) => patchRecord(spaceRoots(doc).notebooks, id, patch);
var deleteSpaceNotebookRef = (doc, id) => spaceRoots(doc).notebooks.delete(id);
var getStarred = (doc) => accountRoots(doc).starred.toJSON();
function setStarred(doc, item, starred) {
  const arr = accountRoots(doc).starred;
  doc.transact(() => {
    const items = arr.toArray();
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i].kind === item.kind && items[i].id === item.id) arr.delete(i, 1);
    }
    if (starred) arr.push([{ kind: item.kind, id: item.id }]);
  });
}
var getTabs = (doc) => accountRoots(doc).tabs.toJSON();
function openTab(doc, noteId) {
  doc.transact(() => {
    const tabs = accountRoots(doc).tabs;
    if (!tabs.toArray().some((t) => t.noteId === noteId)) tabs.push([{ noteId }]);
    accountRoots(doc).settings.set("activeTab", noteId);
  });
}
function closeTab(doc, noteId) {
  doc.transact(() => {
    const tabs = accountRoots(doc).tabs;
    const items = tabs.toArray();
    for (let i = items.length - 1; i >= 0; i--) if (items[i].noteId === noteId) tabs.delete(i, 1);
    const settings = accountRoots(doc).settings;
    if (settings.get("activeTab") === noteId) {
      const rest = tabs.toArray();
      settings.set("activeTab", rest.length > 0 ? rest[rest.length - 1].noteId : null);
    }
  });
}
var getFolders = (doc) => sortByOrder(readRecords(notebookRoots(doc).folders));
var getFolder = (doc, id) => readRecord(notebookRoots(doc).folders, id);
var putFolder = (doc, folder) => void writeRecord(notebookRoots(doc).folders, folder.id, folder);
var patchFolder = (doc, id, patch) => patchRecord(notebookRoots(doc).folders, id, patch);
var deleteFolder = (doc, id) => notebookRoots(doc).folders.delete(id);
function getNoteMetas(doc, query = {}) {
  return sortByOrder(readRecords(notebookRoots(doc).notes)).filter(
    (n) => (query.includeTrashed || n.trashedAt == null) && (query.folderId === void 0 || (n.folderId ?? null) === query.folderId)
  );
}
var getNoteMeta = (doc, id) => readRecord(notebookRoots(doc).notes, id);
var putNoteMeta = (doc, meta) => void writeRecord(notebookRoots(doc).notes, meta.id, meta);
var patchNoteMeta = (doc, id, patch) => patchRecord(notebookRoots(doc).notes, id, patch);
var deleteNoteMeta = (doc, id) => notebookRoots(doc).notes.delete(id);
function findDailyNote(doc, date) {
  return getNoteMetas(doc).find((n) => n.kind === "daily" && n.dailyDate === date) ?? null;
}
function folderSubtree(doc, folderId) {
  const folders = getFolders(doc);
  const out = [folderId];
  for (let i = 0; i < out.length; i++) {
    for (const f of folders) if (f.parentId === out[i] && !out.includes(f.id)) out.push(f.id);
  }
  return out;
}
function getTagProps(doc) {
  return notebookRoots(doc).tags.toJSON();
}
var putTagProps = (doc, name, props) => void writeRecord(notebookRoots(doc).tags, name, props);

export { accountRoots, closeTab, createAccountDoc, createNoteDoc, createNotebookDoc, createSpaceDoc, deleteFolder, deleteNoteMeta, deleteNotebookRef, deleteSharedRef, deleteSharedSpaceRef, deleteSpace, deleteSpaceNotebookRef, findDailyNote, folderSubtree, getFolder, getFolders, getFormatVersion, getNoteMeta, getNoteMetas, getNotebookMeta, getNotebookRef, getNotebookRefs, getSettings, getSharedRef, getSharedRefs, getSharedSpaceRefs, getSpace, getSpaceMeta, getSpaceNotebookRef, getSpaceNotebookRefs, getSpaces, getStarred, getTabs, getTagProps, isReadOnlyFormat, noteRoots, notebookRoots, openTab, patchFolder, patchNoteMeta, patchNotebookRef, patchSpace, patchSpaceNotebookRef, putFolder, putNoteMeta, putNotebookRef, putSharedRef, putSharedSpaceRef, putSpace, putSpaceNotebookRef, putTagProps, rootNames, setNotebookMeta, setSetting, setSpaceMeta, setStarred, spaceRoots, withBlockIds };
//# sourceMappingURL=chunk-7U33VFN2.js.map
//# sourceMappingURL=chunk-7U33VFN2.js.map